import React from "react";

import { Typography } from "@material-ui/core";

import {
  AddIcon,
  AttachFileIcon,
  CopyIcon,
  EditIcon,
  ExportIcon,
  LowInventoryAlertIcon,
  OKIcon,
  RemoveIcon,
  UploadIcon,
} from "../../../components/common/iconography";

export const ContactInfoHelp = ({ classes }) => (
  <>
    <Typography variant="overline">
      <strong>Contact Info</strong>
    </Typography>
    <Typography variant="subtitle1">
      An Account is required to exist before you can create a new Inbound /
      Outbound Shipment, or add Inventory.
    </Typography>
    <ul>
      <li>
        <Typography>
          <strong>Account Name & Address: </strong>
          Required Fields.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Email: </strong>
          Primary contact Email must be unique to this Account.
        </Typography>
      </li>
    </ul>
  </>
);

export const HistoryHelp = ({ classes }) => (
  <>
    <Typography variant="overline">
      <strong>History Log</strong>
    </Typography>
    <Typography variant="subtitle1">
      The History Tab provides an automated, chronological Audit Trail of key
      events and record changes.
    </Typography>
  </>
);

export const CustomSettings = ({ classes }) => (
  <>
    <Typography variant="overline">
      <strong>Custom Settings:</strong>
    </Typography>
    <Typography variant="subtitle1">
      This section allow you to customize platform behavior to meet unique,
      Account-specific requirements.
    </Typography>
    <ul>
      <li>
        <Typography variant="overline">
          <strong>Inbound / Outbound Fields: </strong>
        </Typography>
        <ul>
          <li>
            <Typography>
              <strong>Field Name: </strong>
              Custom Fileld Name to be added to Inbound / Outbound Shipment
              form.
            </Typography>
          </li>
          <li>
            <Typography>
              <strong>Display On: </strong>
              Select whether to display on the mobile Operator App, or Manager
              portal only.
            </Typography>
          </li>
        </ul>
      </li>
      <li>
        <Typography variant="overline">
          <strong>Inventory Fields: </strong>
        </Typography>
        <ul>
          <li>
            <Typography>
              <strong>Field Name: </strong>
              Custom Fileld Name to be added to Tally Sheet form of an Inbound
              Shipment. This fild name will carry over to Stored Inventory, and
              Outbound Pick List items. Custom inventory Fields can be defined
              here and also added directly into Product Catalog. In order for
              the filed to be visible / selectable in Inventory, Product Catalog
              Search, and Inventory Search Lists, it must be defined here.
            </Typography>
          </li>
          <li>
            <Typography>
              <strong>Display On: </strong>
              Select whether to display on the mobile Operator App / Manager
              portal only, and Tally Sheet by default. When editing the Tally
              Sheet, you can override the show/hide settings, by selecting
              custom fields to display.
            </Typography>
          </li>
        </ul>
      </li>
      <li>
        <Typography variant="overline">
          <strong>Document Templates: </strong>
        </Typography>
        <ul>
          <li>
            <Typography>
              <strong>Bill Of Lading Template: </strong>
              Upload a custom BOL template to override default document
              generation template. Follow instructions to download, customize,
              and upload the template file.
            </Typography>
          </li>
        </ul>
      </li>
    </ul>
  </>
);

export const BillingSetupHelp = ({ classes }) => (
  <>
    <Typography variant="overline">
      <strong>Billing Setup</strong>
    </Typography>
    <ul>
      <li>
        <Typography>
          <strong>Lot No. Prefix: </strong>
          Lot Number Prefix must be unique to this account. Used for generating
          Lot Numbers.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Account Type: </strong>
          Account business categorization.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>External Acct. Number: </strong>
          External reference account number from CRM or other system.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Inventory Storage Alert (Days): </strong>
          Days of free storage before Storage charges will be applied. Once
          inventory exceeds this Day limit in storage, the inventory item will
          be marked as Stale Inventory.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Billing Units: </strong>
          Default billing units of measure for this Account. Can be overridden
          in Product Catalog, or an individual Inbound/Outbound/Invoice record.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Payment Terms: </strong>
          Used for defaulting payment terms in Invoicing.
        </Typography>
      </li>
      <li>
        <Typography variant="overline">
          <strong>Billing Address: </strong>
        </Typography>
        <Typography>
          {" "}
          Fill out if different from contact address, otherwise use{" "}
          <strong>Copy Account Contact Info</strong> to auto-populate. This
          address will be used for Invoicing.
        </Typography>
      </li>
      <li>
        <Typography variant="overline">
          <strong>Billing Contacts: </strong>
        </Typography>
        <Typography>
          Specific contacts for billing purposes. Invoices will be address Attn.
          of such billing contact.
        </Typography>
      </li>
    </ul>
  </>
);

export const ProductHelpEdit = ({ classes }) => (
  <>
    <Typography variant="overline">
      <strong>Product Catalog:</strong>
    </Typography>
    <Typography variant="subtitle1">
      Create or Edit a Product Catalog directory by uploading a .CSV file
      populated with product info obtained from your customers.
    </Typography>
    <ul>
      <li>
        <Typography>
          <strong>Inventory Level Icon: </strong>
          Each product row is marked with an Icon indicating either{" "}
          <LowInventoryAlertIcon className={classes.icon} /> Low Inventory
          Level, or <OKIcon className={classes.icon} /> healthy Inventory Levels
        </Typography>
      </li>
      <li>
        <Typography>
          <AddIcon className={classes.icon} />
          <strong> CREATE PRODUCT: </strong>
          Manually add a single, new Product to the Catalog.
        </Typography>
      </li>
      <li>
        <Typography>
          <UploadIcon className={classes.icon} />
          <strong> UPLOAD CATALOG: </strong>
          Opens an Upload Catalog dialog allowing you to upload a .CSV file
          populated with product info obtained from your customers. Follow
          instructions in the dialog window to complete the upload.
        </Typography>
      </li>
      <li>
        <Typography>
          <ExportIcon className={classes.icon} />
          <strong> EXPORT: </strong>
          Export existing Products into a .CSV file. Exported file can be edited
          and re-uploaded to bulk update all products.
        </Typography>
      </li>
      <li>
        <Typography>
          <CopyIcon className={classes.icon} />
          <strong> CLONE: </strong>
          Copy a single existing Product to create a new, similar product. Edit
          pre-populated inputs as needed and save.
        </Typography>
      </li>
      <li>
        <Typography>
          <EditIcon className={classes.icon} />
          <strong> EDIT: </strong>
          Update an existing Product.
        </Typography>
      </li>
    </ul>
  </>
);

export const ProductHelpCreate = ({ classes }) => (
  <>
    <Typography variant="overline">
      <strong>Product Catalog:</strong>
    </Typography>
    <Typography variant="subtitle1">
      Create a Product Catalog directory by uploading a .CSV file populated with
      product info obtained from your customers.
    </Typography>
    <ul>
      <li>
        <Typography>
          Download Product_Catalog_Template.xlsx spreadsheet, and fill it out
          following the instructions on the Download Page.
        </Typography>
      </li>
      <li>
        <Typography>
          Drop the filled-out .CSV file onto the upload zone or click the{" "}
          <AttachFileIcon className={classes.icon} /> icon to select the file.
        </Typography>
      </li>
    </ul>
  </>
);

export const ServiceRatesHelp = ({ classes }) => (
  <>
    <Typography variant="overline">
      <strong>Service Rates:</strong>
    </Typography>
    <Typography variant="subtitle1">
      Capture negotiated service / product / consumables rates. Rates will be
      used as defaults for Invoicing. Accessorial Charges represent a "catch
      all" miscellaneous charges.
    </Typography>
    <ul>
      <li>
        <Typography>
          <strong>Service Name: </strong>
          Short name of service / goods / consumables.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Description: </strong>
          Brief specification of services / goods.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Tax Rate: </strong>
          Select an applicable tax rate to be applied to each service / goods.
          Tax Rates are configured in My Yard setup.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Service Rates: </strong>
          Enter applicable service rates per units utilized for billing of a
          given commodity.
        </Typography>
      </li>
      <li>
        <Typography>
          <AddIcon className={classes.icon} />
          <strong> ADD: </strong>
          Add a new, custom Service definition.
        </Typography>
      </li>
      <li>
        <Typography>
          <RemoveIcon className={classes.icon} />
          <strong> REMOVE: </strong>
          Remove selected Service Item - this info will be permanently lost. No
          other reccords will be affected. Accessorial Charges, Inside Handling,
          and Outside Handling cannot be removed as they are integral to proper
          functioning of the platform.
        </Typography>
      </li>
    </ul>
  </>
);
